import { formatLabel } from '@/utils/common'
import _, { find, get, includes } from 'lodash'
import { IsAdminRole, isInspectorRole, IsSupplierRole } from '@/utils/users'
import { InspectionStatus } from '@/constant/qsf'

const { SUBMITTED, APPROVED, TO_PLAN, PLANNED } = InspectionStatus

/**
 * The user can approve report if:
 * - The user is admin or inspector role and inspector creation inspection
 * @param user
 * @param inspection
 * @returns boolean
 */
export const canUserApproveReport = (user: any, inspection: any) => {
  const isAdmin = IsAdminRole(user)
  const isInspector = isInspectorRole(user)
  const isSupplier = IsSupplierRole(user)

  const isOwnerCreation = inspection?.created_by?.id === user?.id
  let result = false

  if (
    isAdmin ||
    (isOwnerCreation && isInspector) ||
    (isOwnerCreation && isSupplier)
  ) {
    result = true
  }

  return result
}

/**
 * The user can delete report if:
 * - The user is admin, or
 * - The user is an inspector who created the inspection, or
 * - The user is a supplier who created the inspection
 * @param user
 * @param inspection
 * @returns
 */
export const canUserDeleteReport = (user: any, inspection: any) => {
  const isAdmin = IsAdminRole(user)
  const isInspector = isInspectorRole(user)
  const isSupplier = IsSupplierRole(user)
  const isOwnerCreation = inspection?.created_by?.id === user?.id
  const inspectionStatus = includes(
    [TO_PLAN, SUBMITTED, PLANNED, APPROVED],
    inspection?.status
  )

  const isAllowed =
    isAdmin ||
    (isOwnerCreation && isInspector) ||
    (isOwnerCreation && isSupplier) // Supplier can delete their own inspections

  return isAllowed && inspectionStatus
}

/**
 * Determines if the "Approve" feature should be enable for the user
 * The feature is enable if:
 * - The report's status is 'SUBMITTED' and canUserApproveReport
 *
 * @param user
 * @param inspection
 * @returns
 */

export const approveFeatureEnabled = (user: any, inspection: any) => {
  const isAllowToApproveReport = canUserApproveReport(user, inspection)
  const inspectionStatus = includes([SUBMITTED], inspection?.status)
  let result = false

  if (isAllowToApproveReport && inspectionStatus) {
    result = true
  }

  return result
}

/**
 * Determines if the "Unlock" feature should be enable for the user
 * The feature is enable if:
 * - The report's status is 'APPROVED' and canUserApproveReport
 *
 * @param user
 * @param inspection
 * @returns
 */

export const unlockFeatureEnabled = (user: any, inspection: any) => {
  const isAllowToUnlockReport = canUserApproveReport(user, inspection)
  const inspectionStatus = includes([APPROVED], inspection?.status)
  let result = false

  if (isAllowToUnlockReport && inspectionStatus) {
    result = true
  }

  return result
}

export const deleteFeatureEnabled = (user: any, inspection: any) => {
  const isAllowToDeleteReport = canUserDeleteReport(user, inspection)

  let result = false

  if (isAllowToDeleteReport) {
    result = true
  }

  return result
}

export const traversal = (data = {}): any => {
  if (data == null) return []
  return Object.entries(data).reduce((buf: any[], [key, value]: any) => {
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      if (value == true) {
        value = 'Yes'
      } else if (value == false) {
        value = 'No'
      }
      return buf.concat({
        key,
        title: formatLabel(key),
        value: value,
      })
    }
    const children = traversal(value)
    return buf.concat(children)
  }, [])
}

export const getSectionDataByReportKey = (sectionData, reportKey) => {
  return get(
    find(get(sectionData, 'inspectionSections'), {
      reportKey: reportKey,
    }),
    'sectionData'
  )
}

export const groupByName = (sections: any[]) => {
  return _.groupBy(sections, 'sectionName')
}

export const getOverallResult = (section: any[]) => {
  const first = _.first(section)
  return _.get(first, 'sectionData.overallResult')
}

export const sortByIndex = (sections: any[]) => {
  return _.sortBy(sections, 'index')
}

export const getDownloadCAPUrl = (inspectionId: string) => {
  const params = new URLSearchParams({
    name: 'inspections_export_supplier_cap',
    flow: 'force_download',
    inspection_id: inspectionId,
  })
  return `/export-data?${params.toString()}`
}
